/**
 * NDT AI digests (batch 6, fieldiq-api#1177 / #1178): names, skins, who may
 * press what, and the few formatters the digest pages share.
 *
 * NOTHING HERE COMPUTES A FIGURE. Every rate, count, flag, dose and days-left
 * is the API's deterministic layer; this file words them. The AI's part is the
 * draft text only, and it is shown as a draft until a person approves it.
 */
import { fmtQty } from './display-labels';
import { NDT_UNKNOWN_SKIN, type NdtSkin } from './ndt-inspection';
import type { NdtAiAgent, NdtDoseStatus, NdtRepairFlagKind } from '../types/ndt-ai';

/** Mirrors fieldiq-api lib/ndt-roles.ts NDT_AI_ROLES (the agent-review roles). For hiding, not enforcing. */
export const NDT_AI_ROLES = ['admin', 'office_staff', 'supervisor'] as const;
export const canUseNdtAi = (role: string | null | undefined): boolean => !!role && (NDT_AI_ROLES as readonly string[]).includes(role);

export const NDT_AI_AGENT_LABEL: Record<NdtAiAgent, string> = {
  ndt_repair_rate_digest: 'Repair-rate digest',
  ndt_cert_expiry_digest: 'Compliance digest',
  ndt_dose_review: 'Dose review',
  ndt_ticket_preflight: 'Ticket preflight',
};
export const ndtAiAgentLabel = (v: string | null | undefined): string =>
  v && Object.prototype.hasOwnProperty.call(NDT_AI_AGENT_LABEL, v) ? NDT_AI_AGENT_LABEL[v as NdtAiAgent] : '—';

/**
 * Review outcome (agent_runs.outcome). Pending amber (waiting on a person),
 * approved green, dismissed and superseded slate (a newer run of the same
 * digest replaced it) — the label tells those two apart.
 */
export const NDT_AI_OUTCOME_SKIN: Record<string, NdtSkin> = {
  pending: { label: 'Needs review', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  actioned: { label: 'Approved', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  dismissed: { label: 'Dismissed', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  superseded: { label: 'Superseded', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  no_action: { label: 'No action', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const ndtAiOutcomeSkin = (v: string | null | undefined): NdtSkin =>
  v && Object.prototype.hasOwnProperty.call(NDT_AI_OUTCOME_SKIN, v) ? NDT_AI_OUTCOME_SKIN[v] : NDT_UNKNOWN_SKIN;

/** A compliance item's status, across the four lists the digest returns. */
export const NDT_AI_ITEM_SKIN: Record<string, NdtSkin> = {
  expired: { label: 'Expired', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  lapsed: { label: 'Lapsed', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  overdue: { label: 'Overdue', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  due_soon: { label: 'Due soon', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  never: { label: 'Never calibrated', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const ndtAiItemSkin = (v: string | null | undefined): NdtSkin =>
  v && Object.prototype.hasOwnProperty.call(NDT_AI_ITEM_SKIN, v) ? NDT_AI_ITEM_SKIN[v] : NDT_UNKNOWN_SKIN;

/** A worker's dose against the investigation level. null = no level configured, never a guessed "Below". */
export const NDT_AI_DOSE_SKIN: Record<NdtDoseStatus, NdtSkin> = {
  over_level: { label: 'At or over level', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  approaching_level: { label: 'Approaching', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  below_level: { label: 'Below level', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
};
const NO_LEVEL_SKIN: NdtSkin = { label: 'No level set', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };
export const ndtAiDoseSkin = (v: string | null | undefined): NdtSkin =>
  v === null || v === undefined ? NO_LEVEL_SKIN : Object.prototype.hasOwnProperty.call(NDT_AI_DOSE_SKIN, v) ? NDT_AI_DOSE_SKIN[v as NdtDoseStatus] : NDT_UNKNOWN_SKIN;

export const NDT_REPAIR_FLAG_TEXT: Record<NdtRepairFlagKind, string> = {
  welder_above_peers: 'Welder well above the rest',
  line_above_peers: 'Line well above the rest',
  month_increase: 'Up on the month before',
};
export const ndtRepairFlagText = (v: string | null | undefined): string =>
  v && Object.prototype.hasOwnProperty.call(NDT_REPAIR_FLAG_TEXT, v) ? NDT_REPAIR_FLAG_TEXT[v as NdtRepairFlagKind] : '—';

/** Fraction points the API computed (0.07) as "7 pts". Formatting only. */
export function ndtPointsText(points: number | null | undefined): string {
  if (typeof points !== 'number' || !Number.isFinite(points)) return '—';
  return `${fmtQty(points * 100, 1)} pts`;
}

/**
 * TENANT UNITS. The dose review returns canonical figures (`deepDoseMsv`,
 * `readingUsvH`) with no meta, while every other NDT read — Dosimetry included —
 * arrives already in the tenant's unit. These two print the digest's canonical
 * figure in the tenant's unit, mirroring fieldiq-api lib/ndt-units.ts exactly
 * (msvForDisplay: imperial ×100 → mrem at 2 dp, metric mSv at 4 dp;
 * usvHForDisplay: imperial ÷10 → mR/h at 4 dp, metric µSv/h at 3 dp), so a
 * figure here reads the same as the same figure on Dosimetry. The unit system
 * is the NDT meta's (`meta.unitSystem`, resolved by the API's ndtMeta). Unit
 * conversion only — no figure is derived. Unknown unit system (meta not loaded
 * or refused): the canonical value with its canonical unit, which is true.
 */
export const NDT_MREM_PER_MSV = 100;
export const NDT_USV_PER_MR = 10;
type UnitSystem = 'metric' | 'imperial' | null | undefined;
const roundTo = (n: number, places: number) => Math.round(n * 10 ** places) / 10 ** places;

/** A dose the API returned in mSv, in the tenant's unit. Absent → `—`; a real 0 is `0 mSv` / `0 mrem`. */
export function ndtDoseText(msv: number | null | undefined, unitSystem: UnitSystem): string {
  if (typeof msv !== 'number' || !Number.isFinite(msv)) return '—';
  if (unitSystem === 'imperial') return `${fmtQty(roundTo(msv * NDT_MREM_PER_MSV, 2), 2)} mrem`;
  return `${fmtQty(roundTo(msv, 4), 4)} mSv`;
}

/** A survey rate the API returned in µSv/h, in the tenant's unit (mR/h on imperial). */
export function ndtSurveyRateText(usvH: number | null | undefined, unitSystem: UnitSystem): string {
  if (typeof usvH !== 'number' || !Number.isFinite(usvH)) return '—';
  if (unitSystem === 'imperial') return `${fmtQty(roundTo(usvH / NDT_USV_PER_MR, 4), 4)} mR/h`;
  if (unitSystem === 'metric') return `${fmtQty(roundTo(usvH, 3), 3)} µSv/h`;
  return `${fmtQty(usvH, 4)} µSv/h`;
}

/** `3 days left`, `12 days ago`, `Today`. From the API's days-left; null → `—`. */
export function ndtDaysLeftText(days: number | null | undefined): string {
  if (typeof days !== 'number' || !Number.isFinite(days)) return '—';
  if (days === 0) return 'Today';
  if (days < 0) return `${Math.abs(days)} day${days === -1 ? '' : 's'} ago`;
  return `${days} day${days === 1 ? '' : 's'} left`;
}

/** What a stored run covered, from its trigger data: a window, a day, or a ticket. */
export function ndtAiScopeText(agent: string, triggerData: Record<string, unknown> | null, day: (v: string | null) => string): string {
  const t = triggerData ?? {};
  const str = (k: string) => (typeof t[k] === 'string' ? (t[k] as string) : null);
  if (agent === 'ndt_ticket_preflight') return str('ticketNumber') ?? '—';
  if (agent === 'ndt_cert_expiry_digest') return str('today') ? `As of ${day(str('today'))}` : '—';
  const from = str('from');
  const to = str('to');
  return from && to ? `${day(from)} – ${day(to)}` : '—';
}
